import { useState } from 'react';

import {
  deleteSiteCmsImageApi,
  listSiteCmsImagesApi,
  updateSiteCmsImageApi,
  uploadSiteCmsImageApi,
} from '@/lib/api/siteCmsApi';
import { cmsImageReferences, defaultCmsBranding } from '@shared/cms/content';
import { cmsResourceLocations } from '@shared/cms/resource-usage';
import type { SiteCmsDraftResponse, SiteCmsImageResponse } from '@shared/types/entities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ImagePlus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { SiteCmsDialog } from './SiteCmsDialog';
import SiteCmsImagePicker from './SiteCmsImagePicker';
import SiteCmsPrivateImage from './SiteCmsPrivateImage';
import SiteCmsStorageQuota from './SiteCmsStorageQuota';
import { cmsImageFileIssue, cmsInsertUploadedImage, cmsSearchMatches } from './site-cms-interaction-model';
import { cmsBytes, cmsStorageFileIssue } from './site-cms-storage-model';
import { cmsKeepIdentity } from './site-cms-workspace-model';
import { useCmsBusyOperation, useCmsInteractions, useCmsPendingForm } from './use-cms-interactions';
import { cmsMediaUsageKey, useCmsMediaUsage } from './use-cms-media-usage';

export { default as SiteCmsPrivateImage } from './SiteCmsPrivateImage';

export default function SiteCmsImages({
  draft,
  onMetadataChanged,
  onChange,
}: {
  draft: SiteCmsDraftResponse;
  onMetadataChanged: () => void;
  onChange?: (draft: SiteCmsDraftResponse) => void;
}) {
  const { ownerType, ownerId } = draft;
  const key = ['site-cms', 'images', ownerType, ownerId];
  const client = useQueryClient();
  const { ask } = useCmsInteractions();
  const usage = useCmsMediaUsage(ownerType, ownerId);
  const quota = usage.data?.images;
  const quotaUnavailable = usage.isError || !quota;
  const refreshUsage = () => {
    void client.invalidateQueries({ queryKey: cmsMediaUsageKey(ownerType, ownerId) });
  };
  const library = useQuery({ queryKey: key, queryFn: () => listSiteCmsImagesApi(ownerType, ownerId), retry: false });
  const [title, setTitle] = useState('');
  const [alt, setAlt] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [inputKey, setInputKey] = useState(0);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<SiteCmsImageResponse | null>(null);
  const [failure, setFailure] = useState('');
  const original = library.data?.find(i => i.id === editing?.id);
  const metadataDirty = Boolean(editing && (editing.title !== original?.title || editing.alt !== original?.alt));
  useCmsPendingForm('image-upload', Boolean(file || title || alt));
  useCmsPendingForm('image-metadata', metadataDirty);
  const used = cmsImageReferences(draft.pages, draft.branding);
  const upload = useMutation({
    mutationFn: (selected: File) => uploadSiteCmsImageApi(ownerType, ownerId, title.trim(), alt.trim(), selected),
    onSuccess: image => {
      client.setQueryData<SiteCmsImageResponse[]>(key, rows => cmsInsertUploadedImage(rows, image));
      void client.invalidateQueries({ queryKey: key });
      setFile(null);
      setTitle('');
      setAlt('');
      setInputKey(x => x + 1);
      setFailure('');
      toast.success('Image ajoutée à la bibliothèque privée.');
    },
    onError: error => setFailure(error.message),
    onSettled: refreshUsage,
  });
  const update = useMutation({
    mutationFn: (image: SiteCmsImageResponse) =>
      updateSiteCmsImageApi(ownerType, ownerId, image.id, image.title, image.alt),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: key });
      setEditing(null);
      onMetadataChanged();
      setFailure('');
      toast.success('Description enregistrée dans la bibliothèque. Le site public n’a pas changé.');
    },
    onError: error => setFailure(error.message),
    onSettled: refreshUsage,
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteSiteCmsImageApi(ownerType, ownerId, id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: key });
      setFailure('');
      toast.success('Image supprimée.');
    },
    onError: error => setFailure(error.message),
    onSettled: refreshUsage,
  });
  useCmsBusyOperation('image-library', upload.isPending || update.isPending || remove.isPending);
  const fileIssue = cmsImageFileIssue(file, library.data?.length ?? 0) ?? cmsStorageFileIssue('images', file, quota);
  const images = library.data ?? [];
  const results = images.filter(i => cmsSearchMatches(`${i.title} ${i.alt}`, search));
  async function close() {
    if (update.isPending) return;
    if (
      metadataDirty &&
      (await ask({
        title: 'Abandonner la description ?',
        message: 'La description saisie n’a pas encore été enregistrée dans la bibliothèque.',
        choices: [{ value: 'close', label: 'Abandonner ces champs', danger: true }],
      })) !== 'close'
    )
      return;
    setEditing(null);
  }
  async function replaceAll(id: string | null) {
    if (!editing || !id || id === editing.id || !onChange) return;
    const old = editing.id;
    const locations = cmsResourceLocations('image', old, draft.pages, draft.headerMenu, draft.branding);
    if (
      (await ask({
        title: 'Remplacer dans le brouillon ?',
        message: `${locations.length} utilisation(s) concernée(s) : ${locations.join(' ; ')}. Les versions déjà publiées restent inchangées.`,
        choices: [{ value: 'replace', label: 'Remplacer ces occurrences' }],
      })) !== 'replace'
    )
      return;
    const brand = { ...defaultCmsBranding(draft.theme), ...draft.branding };
    onChange({
      ...draft,
      branding: {
        ...brand,
        logoImageId: brand.logoImageId === old ? id : brand.logoImageId,
        coverImageId: brand.coverImageId === old ? id : brand.coverImageId,
        backgroundImageId: brand.backgroundImageId === old ? id : brand.backgroundImageId,
      },
      pages: draft.pages.map(page => ({
        ...page,
        blocks: page.blocks.map(block => {
          if (block.type === 'cover' && (block.imageId === old || block.backgroundImageId === old))
            return cmsKeepIdentity(block, {
              ...block,
              imageId: block.imageId === old ? id : block.imageId,
              backgroundImageId: block.backgroundImageId === old ? id : block.backgroundImageId,
            });
          if (block.type === 'imageText' && block.imageId === old)
            return cmsKeepIdentity(block, { ...block, imageId: id });
          if (block.type === 'partners')
            return cmsKeepIdentity(block, {
              ...block,
              items: block.items.map(item => (item.imageId === old ? { ...item, imageId: id } : item)),
            });
          return block;
        }),
      })),
    });
    setEditing(null);
  }
  return (
    <div className='cms-stack'>
      {failure && (
        <div role='alert' className='cms-alert'>
          {failure}
        </div>
      )}
      <SiteCmsStorageQuota
        kind='images'
        quota={quota}
        loading={usage.isPending}
        failed={usage.isError}
        refreshing={usage.isFetching}
        retry={() => void usage.refetch()}
      />
      <section className='cms-card'>
        <div className='cms-card-heading'>
          <div>
            <span className='cms-kicker'>Bibliothèque privée</span>
            <h3>Images & logos</h3>
          </div>
          <ImagePlus size={24} />
        </div>
        <p className='cms-muted'>
          Importer ne publie rien. Choisissez ensuite où utiliser l’image dans votre brouillon.
        </p>
        <details className='cms-import-details'>
          <summary>Ajouter une image</summary>
          <fieldset
            disabled={upload.isPending || library.isPending || library.isError || quotaUnavailable}
            className='cms-form-grid'
          >
            <label>
              Titre de l’image
              <input
                maxLength={150}
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder='Le terrain de notre association'
              />
            </label>
            <label>
              Fichier
              <input
                key={inputKey}
                type='file'
                accept='image/jpeg,image/png,image/webp'
                onChange={e => {
                  const next = e.target.files?.[0] ?? null;
                  setFile(next);
                  if (next && !title) setTitle(next.name.replace(/\.[^.]+$/, '').slice(0, 150));
                }}
              />
            </label>
            <label className='cms-span-two'>
              Description alternative
              <textarea
                maxLength={250}
                rows={2}
                value={alt}
                onChange={e => setAlt(e.target.value)}
                placeholder='Décrivez la photo pour une personne qui ne la voit pas.'
              />
            </label>
            <button
              type='button'
              className='cms-button cms-button-primary'
              disabled={
                !file ||
                !title.trim() ||
                Boolean(fileIssue) ||
                quotaUnavailable ||
                images.length >= (quota?.maxCount ?? 100)
              }
              onClick={() => {
                if (!file) return;
                if (fileIssue) {
                  setFailure(fileIssue);
                  return;
                }
                upload.mutate(file);
              }}
            >
              {upload.isPending ? 'Optimisation…' : 'Ajouter l’image'}
            </button>
          </fieldset>
          {fileIssue && (
            <p className='cms-alert' role='alert'>
              {fileIssue}
            </p>
          )}
          {(file || title || alt) && (
            <button
              type='button'
              className='cms-link-button'
              disabled={upload.isPending}
              onClick={() => {
                setFile(null);
                setTitle('');
                setAlt('');
                setInputKey(x => x + 1);
              }}
            >
              Annuler cet import
            </button>
          )}
        </details>
        <p className='cms-hint'>
          JPEG, PNG ou WebP non animé · 25 mégapixels maximum.
          {file ? ` Fichier sélectionné : ${cmsBytes(file.size)} avant optimisation.` : ''}
        </p>
      </section>
      <section className='cms-card'>
        <div className='cms-card-heading'>
          <h3>Vos images</h3>
          <label className='cms-search'>
            <input
              aria-label='Rechercher une image'
              value={search}
              placeholder='Rechercher…'
              onChange={e => setSearch(e.target.value)}
            />
          </label>
        </div>
        {library.isPending ? (
          <p role='status'>Chargement de la bibliothèque…</p>
        ) : library.isError ? (
          <p role='alert' className='cms-alert'>
            Bibliothèque indisponible.{' '}
            <button type='button' onClick={() => void library.refetch()}>
              Réessayer
            </button>
          </p>
        ) : (
          <>
            <div className='cms-image-grid'>
              {results.map(image => {
                const locked = used.has(image.id) || image.usedInDraft || image.retainedInPublication;
                return (
                  <article className='cms-image-card' key={image.id}>
                    <SiteCmsPrivateImage
                      ownerType={ownerType}
                      ownerId={ownerId}
                      id={image.id}
                      alt={image.alt || image.title}
                    />
                    <div>
                      <strong>{image.title}</strong>
                      <small>
                        {image.width} × {image.height} · {Math.ceil(image.sizeBytes / 1024)} Ko
                      </small>
                      <span className='cms-pill'>
                        {used.has(image.id)
                          ? 'Dans le travail en cours'
                          : image.usedInDraft
                            ? 'Dans le brouillon enregistré'
                            : image.usedInLive
                              ? 'Dans la version en ligne'
                              : image.retainedInPublication
                                ? 'Conservée pour une version'
                                : 'Bibliothèque uniquement'}
                      </span>
                      <div className='cms-media-actions'>
                        <button type='button' className='cms-button' onClick={() => setEditing(image)}>
                          <Pencil size={15} />
                          Détails & usages
                        </button>
                        <button
                          type='button'
                          className='cms-icon-button cms-danger'
                          title={locked ? 'Consultez les usages avant de supprimer' : 'Supprimer'}
                          aria-label={`Supprimer ${image.title}`}
                          disabled={Boolean(locked) || remove.isPending}
                          onClick={async () => {
                            if (
                              (await ask({
                                title: `Supprimer « ${image.title} » ?`,
                                message:
                                  'Le fichier sera supprimé de la bibliothèque privée. Cette action ne remplace aucune image publiée.',
                                choices: [{ value: 'delete', label: 'Supprimer l’image', danger: true }],
                              })) === 'delete'
                            )
                              remove.mutate(image.id);
                          }}
                        >
                          <Trash2 size={17} />
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
            {!results.length && (
              <div className='cms-empty'>
                <ImagePlus size={28} />
                <h4>{images.length ? 'Aucune image trouvée' : 'Votre identité commence ici'}</h4>
                <p>
                  {images.length
                    ? 'Essayez un autre mot dans la recherche.'
                    : 'Ajoutez un logo ou une photo. Une présentation sans photo reste possible.'}
                </p>
              </div>
            )}
          </>
        )}
        <p className='cms-hint'>
          Une image conservée par une version ne peut pas être supprimée ici. Cette protection permet la restauration ;
          la purge des anciennes versions reste une opération administrée.
        </p>
      </section>
      {editing && (
        <SiteCmsDialog
          title='Image : description et utilisations'
          onClose={() => void close()}
          busy={update.isPending}
          wide
        >
          <fieldset disabled={update.isPending} className='cms-edit-fields'>
            <div className='cms-media-detail'>
              <SiteCmsPrivateImage ownerType={ownerType} ownerId={ownerId} id={editing.id} alt={editing.alt} />
              <div>
                <label>
                  Titre
                  <input
                    value={editing.title}
                    maxLength={150}
                    onChange={e => setEditing({ ...editing, title: e.target.value })}
                  />
                </label>
                <label>
                  Description alternative
                  <textarea
                    rows={3}
                    value={editing.alt}
                    maxLength={250}
                    onChange={e => setEditing({ ...editing, alt: e.target.value })}
                  />
                </label>
                <p className='cms-hint'>
                  Ces métadonnées sont enregistrées immédiatement dans la bibliothèque. Le site public ne change
                  qu’après une nouvelle publication.
                </p>
                <button
                  type='button'
                  className='cms-button cms-button-primary'
                  disabled={!editing.title.trim() || update.isPending || !metadataDirty}
                  onClick={() => update.mutate(editing)}
                >
                  Enregistrer la description
                </button>
              </div>
            </div>
            <h3>Utilisations</h3>
            <ul>
              {cmsResourceLocations('image', editing.id, draft.pages, draft.headerMenu, draft.branding).map(label => (
                <li key={label}>Travail en cours · {label}</li>
              ))}
              {(editing.usages ?? []).map((usage, index) => (
                <li key={index}>
                  {usage.scope === 'saved'
                    ? 'Brouillon enregistré'
                    : usage.scope === 'live'
                      ? 'En ligne'
                      : `Version ${usage.revision} conservée`}{' '}
                  · {usage.label}
                </li>
              ))}
            </ul>
            {used.has(editing.id) && onChange && (
              <details>
                <summary>Remplacer toutes les occurrences du brouillon</summary>
                <SiteCmsImagePicker
                  ownerType={ownerType}
                  ownerId={ownerId}
                  value={null}
                  onChange={id => void replaceAll(id)}
                />
              </details>
            )}
            {failure && (
              <p role='alert' className='cms-alert'>
                {failure}
              </p>
            )}
          </fieldset>
        </SiteCmsDialog>
      )}
    </div>
  );
}
